import { ReactNode, useCallback, useMemo } from "react";
import { invalidateAfterLifecycleAction } from "@api/scheduleTemplate.api";
import { InjectedCommandOption } from "@components/commands/lib";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { useScope } from "@state/scopeContext.lib";
import { useBulkScheduleApply } from "../bulk/useBulkScheduleApply";
import { useScheduleHubScope } from "../ScheduleHubScopeProvider";
import { matchesScheduleHubScope } from "../scheduleHubScope";
import { resolveInvoiceTemplateBlockReason } from "../scheduleApplyConfirmHelpers";
import {
    getScheduleChangePolicyListReason,
    partitionScheduleRowsForPolicyChange,
} from "./scheduleApplicationBulkScheduleRow";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import { formatScheduleBulkSkippedWarning } from "./scheduleLifecycleBulkCopy";
import { getUniqueScheduleApplicationIds } from "./scheduleApplicationRowId";

const TOAST_KEY = "schedule-applications-change-policy";

export function useScheduleApplicationsBulkChangePolicy(options: {
    selectedRows: ScheduleApplicationParentRow[];
    clearSelection: () => void;
}): {
    options: InjectedCommandOption[];
    disabledReason: string | undefined;
    confirmModal: ReactNode;
} {
    const { selectedRows, clearSelection } = options;
    const { workspaceId } = useScope();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const partition = useMemo(
        () => partitionScheduleRowsForPolicyChange(selectedRows),
        [selectedRows],
    );
    const invalidate = useCallback(async () => {
        invalidateAfterLifecycleAction(workspaceId);
    }, [workspaceId]);
    const scope = useScheduleHubScope();
    const isTemplateEligible = useCallback(
        (template: ScheduleTemplateListItem) =>
            matchesScheduleHubScope(template, scope),
        [scope],
    );
    const {
        options: policyOptions,
        isLoadingTemplates,
        hasTemplatesError,
        confirmModal,
    } = useBulkScheduleApply({
        rows: partition.rows,
        skippedWarning: formatScheduleBulkSkippedWarning({
            skipped: partition.skipped,
            selectedCount: getUniqueScheduleApplicationIds(selectedRows).length,
        }),
        noun: "schedule",
        isEnabled: true,
        isFieldLocked: false,
        templateBlockReason: partition.rows.some(
            (row) => row.source.sourceType === ScheduleSourceType.invoice,
        )
            ? resolveInvoiceTemplateBlockReason
            : undefined,
        isTemplateEligible,
        toastKey: TOAST_KEY,
        invalidate,
        clearSelection,
    });
    const guardedOptions = useMemo(
        () =>
            policyOptions.map((option) => ({
                ...option,
                onSelect: () => {
                    if (!refuseIfViewOnly()) {
                        option.onSelect();
                    }
                },
            })),
        [policyOptions, refuseIfViewOnly],
    );
    const disabledReason =
        partition.disabledReason ??
        getScheduleChangePolicyListReason({
            selectedCount: selectedRows.length,
            optionCount: policyOptions.length,
            isLoadingTemplates,
            hasTemplatesError,
        });

    return { options: guardedOptions, disabledReason, confirmModal };
}
